import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Bell,
	Building2,
	Clock,
	Code,
	Copy,
	Loader2,
	Mail,
	Send,
	Shield,
	User,
	Users,
	X,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";

// Local types for settings
interface TeamMember {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	role: "owner" | "admin" | "viewer";
	status: "active" | "inactive";
	lastActive: string;
	memberOrgId?: string | null;
}

interface PendingInvitation {
	id: string;
	email: string;
	role: "owner" | "admin" | "viewer";
	invitedBy: string;
	invitedAt: string;
	expiresAt: string;
	token: string;
	memberOrgId: string | null;
}

interface Organization {
	id: string;
	name: string;
	type: "buyer" | "member" | "subtier";
}

export const Route = createFileRoute("/settings/")({
	component: SettingsPage,
});

function SettingsPage() {
	const { user, hasPermission } = useAuth();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState("profile");
	const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteRole, setInviteRole] = useState<string>("viewer");
	const [inviteMemberOrg, setInviteMemberOrg] = useState<string>("none");

	// Profile form state
	const [profileForm, setProfileForm] = useState({
		firstName: "",
		lastName: "",
	});

	// Password form state
	const [passwordForm, setPasswordForm] = useState({
		newPassword: "",
		confirmPassword: "",
	});
	const [passwordError, setPasswordError] = useState<string | null>(null);

	// Fetch buyer organization data
	const { data: buyerOrgData, isLoading: orgLoading } = useQuery(
		trpc.organizations.getBuyer.queryOptions({
			id: user?.organizationId ?? "",
		}),
	);

	// Populate profile form when user data loads
	useEffect(() => {
		if (user) {
			setProfileForm({
				firstName: user.firstName ?? "",
				lastName: user.lastName ?? "",
			});
		}
	}, [user]);

	// Update profile mutation
	const updateProfileMutation = useMutation(
		trpc.auth.updateProfile.mutationOptions({
			onSuccess: () => {
				toast.success("Profile saved");
				queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() });
			},
			onError: (error) => {
				toast.error(`Failed to save profile: ${error.message}`);
			},
		}),
	);

	// Change password mutation
	const changePasswordMutation = useMutation(
		trpc.auth.changePassword.mutationOptions({
			onSuccess: () => {
				toast.success("Password updated successfully");
				setPasswordForm({ newPassword: "", confirmPassword: "" });
				setPasswordError(null);
			},
			onError: (error) => {
				toast.error(`Failed to change password: ${error.message}`);
			},
		}),
	);

	// Team members - from tRPC query
	const { data: membersData, isLoading: membersLoading } = useQuery(
		trpc.members.list.queryOptions({
			organizationId: user?.organizationId ?? "",
		}),
	);

	const teamMembers: TeamMember[] = (membersData ?? []).map((m) => ({
		id: m.user_id,
		firstName: m.profiles?.first_name ?? "",
		lastName: m.profiles?.last_name ?? "",
		email: m.profiles?.email ?? "",
		role: m.role as "owner" | "admin" | "viewer",
		status: "active" as const,
		lastActive: m.joined_at,
		memberOrgId: null,
	}));

	// Pending invitations - from tRPC query
	const { data: pendingInvitationsData, isLoading: invitationsLoading } = useQuery(
		trpc.members.pendingInvitations.queryOptions({
			organizationId: user?.organizationId ?? "",
		}),
	);

	const pendingInvitations: PendingInvitation[] = (pendingInvitationsData ?? []).map((inv) => ({
		id: inv.id,
		email: inv.email,
		role: inv.role as "owner" | "admin" | "viewer",
		invitedBy: inv.profiles?.first_name && inv.profiles?.last_name
			? `${inv.profiles.first_name} ${inv.profiles.last_name}`
			: inv.profiles?.email ?? "Unknown",
		invitedAt: inv.created_at,
		expiresAt: inv.expires_at,
		token: "",
		memberOrgId: null,
	}));

	// Member organizations - from tRPC query
	const { data: accessibleOrgs } = useQuery(
		trpc.organizations.getAccessibleOrgs.queryOptions(),
	);

	const memberOrgs: Organization[] = (accessibleOrgs ?? [])
		.filter((org) => org.type === "member")
		.map((org) => ({
			id: org.id,
			name: org.name,
			type: org.type as "buyer" | "member" | "subtier",
		}));

	// Invite mutation
	const inviteMutation = useMutation(
		trpc.members.invite.mutationOptions({
			onSuccess: () => {
				toast.success(`Invitation sent to ${inviteEmail}`);
				setInviteEmail("");
				setInviteRole("viewer");
				setInviteMemberOrg("none");
				setInviteDialogOpen(false);
				queryClient.invalidateQueries({ queryKey: trpc.members.pendingInvitations.queryKey() });
			},
			onError: (error) => {
				toast.error(`Failed to send invitation: ${error.message}`);
			},
		}),
	);

	// Revoke invitation mutation
	const revokeInvitationMutation = useMutation(
		trpc.members.revokeInvitation.mutationOptions({
			onSuccess: () => {
				toast.success("Invitation revoked");
				queryClient.invalidateQueries({ queryKey: trpc.members.pendingInvitations.queryKey() });
			},
			onError: (error) => {
				toast.error(`Failed to revoke invitation: ${error.message}`);
			},
		}),
	);

	// Update member role mutation
	const updateRoleMutation = useMutation(
		trpc.members.updateRole.mutationOptions({
			onSuccess: (data) => {
				toast.success(`Role updated to ${data.role}`);
				queryClient.invalidateQueries({ queryKey: trpc.members.list.queryKey() });
			},
			onError: (error) => {
				toast.error(`Failed to update role: ${error.message}`);
			},
		}),
	);

	// Embed settings state
	const [embedWidth, setEmbedWidth] = useState("100%");
	const [embedHeight, setEmbedHeight] = useState("600px");

	// Fetch notification settings from API
	const { data: notificationSettings, isLoading: notificationSettingsLoading } = useQuery(
		trpc.organizations.getNotificationSettings.queryOptions({
			id: user?.organizationId ?? "",
		}),
	);

	// Email notification preferences state (initialized from API)
	const [notificationPrefs, setNotificationPrefs] = useState({
		newBidSubmissions: true,
		vendorQuestions: true,
	});

	// Sync state with fetched data
	useEffect(() => {
		if (notificationSettings) {
			setNotificationPrefs({
				newBidSubmissions: notificationSettings.emailOnBidSubmission,
				vendorQuestions: notificationSettings.emailOnVendorQuestion,
			});
		}
	}, [notificationSettings]);

	// Update notification settings mutation
	const updateNotificationsMutation = useMutation(
		trpc.organizations.updateNotificationSettings.mutationOptions({
			onSuccess: () => {
				toast.success("Notification preferences saved");
				queryClient.invalidateQueries({ queryKey: trpc.organizations.getNotificationSettings.queryKey() });
			},
			onError: (error) => {
				toast.error(`Failed to save preferences: ${error.message}`);
			},
		}),
	);

	const handleSaveNotifications = () => {
		if (!user?.organizationId) return;
		updateNotificationsMutation.mutate({
			organizationId: user.organizationId,
			emailOnBidSubmission: notificationPrefs.newBidSubmissions,
			emailOnVendorQuestion: notificationPrefs.vendorQuestions,
		});
	};

	const handleSaveProfile = () => {
		updateProfileMutation.mutate({
			firstName: profileForm.firstName,
			lastName: profileForm.lastName,
		});
	};

	const handleChangePassword = () => {
		setPasswordError(null);

		if (passwordForm.newPassword.length < 8) {
			setPasswordError("Password must be at least 8 characters");
			return;
		}

		if (passwordForm.newPassword !== passwordForm.confirmPassword) {
			setPasswordError("Passwords do not match");
			return;
		}

		changePasswordMutation.mutate({
			newPassword: passwordForm.newPassword,
		});
	};

	const handleSendInvite = () => {
		if (!inviteEmail) {
			toast.error("Please enter an email address");
			return;
		}
		if (!inviteEmail.includes("@")) {
			toast.error("Please enter a valid email address");
			return;
		}
		if (!user?.organizationId) {
			toast.error("No organization found");
			return;
		}

		inviteMutation.mutate({
			email: inviteEmail,
			role: inviteRole as "admin" | "viewer",
			organizationId: user.organizationId,
		});
	};

	const handleRevokeInvite = (inviteId: string, _email: string) => {
		if (!user?.organizationId) return;
		revokeInvitationMutation.mutate({
			invitationId: inviteId,
			organizationId: user.organizationId,
		});
	};

	const handleResendInvite = (email: string) => {
		// TODO: Add resend invitation endpoint
		toast.info(`Resend invitation to ${email} - feature coming soon`);
	};

	// Role level helper - owner > admin > viewer
	const getRoleLevel = (role: string) => {
		switch (role) {
			case "owner": return 3;
			case "admin": return 2;
			case "viewer": return 1;
			default: return 0;
		}
	};

	// Get current user's role from members data
	const currentUserRole = teamMembers.find(m => m.id === user?.id)?.role ?? "viewer";
	const currentUserLevel = getRoleLevel(currentUserRole);

	// Check if current user can modify another user's role
	const canModifyRole = (targetRole: string) => {
		const targetLevel = getRoleLevel(targetRole);
		// Can only modify roles of users with lower permissions
		return currentUserLevel > targetLevel;
	};

	// Get available role options for the current user to assign
	const getAssignableRoles = () => {
		// Can only assign roles lower than your own
		const roles: Array<{ value: string; label: string }> = [];
		if (currentUserLevel > getRoleLevel("viewer")) {
			roles.push({ value: "viewer", label: "Viewer" });
		}
		if (currentUserLevel > getRoleLevel("admin")) {
			roles.push({ value: "admin", label: "Admin" });
		}
		// Note: Even owners can't assign owner role via this interface
		// to prevent accidentally giving away ownership
		return roles;
	};

	const handleRoleChange = (memberId: string, newRole: string) => {
		if (!user?.organizationId) return;
		updateRoleMutation.mutate({
			userId: memberId,
			organizationId: user.organizationId,
			role: newRole as "owner" | "admin" | "viewer",
		});
	};

	// Embed code generation
	const orgSlug = buyerOrgData?.slug ?? "";
	const embedUrl = orgSlug ? `https://purchasing.bidscope.ai/public/${orgSlug}/embed` : "";
	const embedCode = orgSlug ? `<iframe
  src="${embedUrl}"
  width="${embedWidth}"
  height="${embedHeight}"
  frameborder="0"
  style="border: 1px solid #e5e7eb; border-radius: 8px;"
  title="${buyerOrgData?.name || "Solicitations"}"
></iframe>` : "";

	const handleCopyEmbedCode = () => {
		navigator.clipboard.writeText(embedCode);
		toast.success("Embed code copied to clipboard!");
	};

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">Settings</h1>
					<p className="text-muted-foreground">
						Manage your account and organization settings
					</p>
				</div>

				<Tabs value={activeTab} onValueChange={setActiveTab}>
					<TabsList className="flex-wrap">
						<TabsTrigger value="profile">
							<User className="mr-2 h-4 w-4" />
							Profile
						</TabsTrigger>
						<TabsTrigger value="organization">
							<Building2 className="mr-2 h-4 w-4" />
							Organization
						</TabsTrigger>
						<TabsTrigger value="team">
							<Users className="mr-2 h-4 w-4" />
							Team
						</TabsTrigger>
						<TabsTrigger value="notifications">
							<Bell className="mr-2 h-4 w-4" />
							Notifications
						</TabsTrigger>
						<TabsTrigger value="security">
							<Shield className="mr-2 h-4 w-4" />
							Security
						</TabsTrigger>
						<TabsTrigger value="embed">
							<Code className="mr-2 h-4 w-4" />
							Embed
						</TabsTrigger>
					</TabsList>

					<TabsContent value="profile" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Personal Information</CardTitle>
								<CardDescription>Update your personal details</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center gap-4">
									<Avatar className="h-20 w-20">
										<AvatarFallback className="text-2xl">
											{profileForm.firstName?.[0]}
											{profileForm.lastName?.[0]}
										</AvatarFallback>
									</Avatar>
								</div>
								<div className="grid gap-4 md:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="firstName">First Name</Label>
										<Input
											id="firstName"
											value={profileForm.firstName}
											onChange={(e) => setProfileForm((prev) => ({ ...prev, firstName: e.target.value }))}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="lastName">Last Name</Label>
										<Input
											id="lastName"
											value={profileForm.lastName}
											onChange={(e) => setProfileForm((prev) => ({ ...prev, lastName: e.target.value }))}
										/>
									</div>
									<div className="space-y-2 md:col-span-2">
										<Label htmlFor="email">Email</Label>
										<Input id="email" type="email" defaultValue={user?.email} disabled />
										<p className="text-xs text-muted-foreground">
											Email cannot be changed
										</p>
									</div>
								</div>
								<Button
									onClick={handleSaveProfile}
									disabled={updateProfileMutation.isPending}
								>
									{updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
								</Button>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="organization" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Public Portal</CardTitle>
								<CardDescription>
									Your organization's public solicitation portal
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{buyerOrgData?.slug ? (
									<div className="space-y-2">
										<Label>Portal URL</Label>
										<div className="flex items-center gap-2">
											<Input
												value={`https://purchasing.bidscope.ai/public/${buyerOrgData.slug}`}
												disabled
												className="font-mono text-sm"
											/>
											<Button
												variant="outline"
												size="icon"
												onClick={() => {
													navigator.clipboard.writeText(`https://purchasing.bidscope.ai/public/${buyerOrgData.slug}`);
													toast.success("Portal URL copied to clipboard");
												}}
											>
												<Copy className="h-4 w-4" />
											</Button>
										</div>
										<p className="text-xs text-muted-foreground">
											This URL is based on your organization name and cannot be changed.
										</p>
									</div>
								) : (
									<div className="text-sm text-muted-foreground">
										<p>No portal URL has been configured for your organization.</p>
										<p className="mt-1">Contact support to have a portal URL assigned.</p>
									</div>
								)}
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="team" className="space-y-6">
						{/* Team Members Card */}
						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<div>
									<CardTitle>Team Members</CardTitle>
									<CardDescription>
										Manage users in your organization
									</CardDescription>
								</div>
								{hasPermission("admin") && (
									<Dialog
										open={inviteDialogOpen}
										onOpenChange={setInviteDialogOpen}
									>
										<DialogTrigger asChild>
											<Button>
												<Send className="mr-2 h-4 w-4" />
												Invite User
											</Button>
										</DialogTrigger>
										<DialogContent>
											<DialogHeader>
												<DialogTitle>Invite User</DialogTitle>
												<DialogDescription>
													Send an invitation to join your organization. They
													will receive an email with a link to accept.
												</DialogDescription>
											</DialogHeader>
											<div className="space-y-4 py-4">
												<div className="space-y-2">
													<Label htmlFor="invite-email">Email Address</Label>
													<Input
														id="invite-email"
														type="email"
														placeholder="colleague@example.gov"
														value={inviteEmail}
														onChange={(e) => setInviteEmail(e.target.value)}
													/>
												</div>
												<div className="space-y-2">
													<Label htmlFor="invite-role">Role</Label>
													<Select
														value={inviteRole}
														onValueChange={setInviteRole}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select a role" />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="viewer">
																<div className="flex flex-col">
																	<span>Viewer</span>
																	<span className="text-xs text-muted-foreground">
																		Can view content but cannot edit
																	</span>
																</div>
															</SelectItem>
															<SelectItem value="admin">
																<div className="flex flex-col">
																	<span>Admin</span>
																	<span className="text-xs text-muted-foreground">
																		Can edit content and manage users
																	</span>
																</div>
															</SelectItem>
														</SelectContent>
													</Select>
												</div>
												<div className="space-y-2">
													<Label htmlFor="invite-member-org">Scope</Label>
													<Select
														value={inviteMemberOrg}
														onValueChange={setInviteMemberOrg}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select scope" />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="none">
																<div className="flex flex-col">
																	<span>Entire Buyer</span>
																	<span className="text-xs text-muted-foreground">
																		Access to all member organizations
																	</span>
																</div>
															</SelectItem>
															{memberOrgs.map((org) => (
																<SelectItem key={org.id} value={org.id}>
																	<div className="flex flex-col">
																		<span>{org.name}</span>
																		<span className="text-xs text-muted-foreground">
																			Scoped to this member only
																		</span>
																	</div>
																</SelectItem>
															))}
														</SelectContent>
													</Select>
													<p className="text-xs text-muted-foreground">
														Optionally limit user access to a specific member
														organization
													</p>
												</div>
											</div>
											<DialogFooter>
												<Button
													variant="outline"
													onClick={() => setInviteDialogOpen(false)}
												>
													Cancel
												</Button>
												<Button onClick={handleSendInvite}>
													<Mail className="mr-2 h-4 w-4" />
													Send Invitation
												</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>
								)}
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>User</TableHead>
											<TableHead>Role</TableHead>
											<TableHead>Scope</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Last Active</TableHead>
											<TableHead className="text-right">Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{teamMembers.map((member) => (
											<TableRow key={member.id}>
												<TableCell>
													<div className="flex items-center gap-3">
														<Avatar className="h-8 w-8">
															<AvatarFallback>
																{member.firstName[0]}
																{member.lastName[0]}
															</AvatarFallback>
														</Avatar>
														<div>
															<p className="font-medium">
																{member.firstName} {member.lastName}
															</p>
															<p className="text-sm text-muted-foreground">
																{member.email}
															</p>
														</div>
													</div>
												</TableCell>
												<TableCell>
													<Badge variant="outline" className="capitalize">
														{member.role}
													</Badge>
												</TableCell>
												<TableCell>
													{member.memberOrgId ? (
														<span className="text-sm">
															{memberOrgs.find(
																(org) => org.id === member.memberOrgId,
															)?.name || "Unknown"}
														</span>
													) : (
														<span className="text-sm text-muted-foreground">
															All
														</span>
													)}
												</TableCell>
												<TableCell>
													<Badge
														variant={
															member.status === "active"
																? "default"
																: "secondary"
														}
													>
														{member.status}
													</Badge>
												</TableCell>
												<TableCell>
													{new Date(member.lastActive).toLocaleDateString()}
												</TableCell>
												<TableCell className="text-right">
													{hasPermission("admin") && member.id !== user?.id && canModifyRole(member.role) ? (
														<Select
															value={member.role}
															onValueChange={(value) => handleRoleChange(member.id, value)}
															disabled={updateRoleMutation.isPending}
														>
															<SelectTrigger className="w-24">
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{getAssignableRoles().map((role) => (
																	<SelectItem key={role.value} value={role.value}>
																		{role.label}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													) : hasPermission("admin") && member.id !== user?.id ? (
														<Badge variant="outline" className="capitalize">
															{member.role}
														</Badge>
													) : null}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</CardContent>
						</Card>

						{/* Pending Invitations Card */}
						{hasPermission("admin") && pendingInvitations.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<Clock className="h-5 w-5" />
										Pending Invitations
									</CardTitle>
									<CardDescription>
										Invitations that have been sent but not yet accepted
									</CardDescription>
								</CardHeader>
								<CardContent>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Email</TableHead>
												<TableHead>Role</TableHead>
												<TableHead>Scope</TableHead>
												<TableHead>Invited By</TableHead>
												<TableHead>Sent</TableHead>
												<TableHead>Expires</TableHead>
												<TableHead className="text-right">Actions</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{pendingInvitations.map((invitation) => (
												<TableRow key={invitation.id}>
													<TableCell>
														<div className="flex items-center gap-2">
															<Mail className="h-4 w-4 text-muted-foreground" />
															<span>{invitation.email}</span>
														</div>
													</TableCell>
													<TableCell>
														<Badge variant="outline" className="capitalize">
															{invitation.role}
														</Badge>
													</TableCell>
													<TableCell>
														{invitation.memberOrgId ? (
															<span className="text-sm">
																{memberOrgs.find(
																	(org) => org.id === invitation.memberOrgId,
																)?.name || "Unknown"}
															</span>
														) : (
															<span className="text-sm text-muted-foreground">
																All
															</span>
														)}
													</TableCell>
													<TableCell>{invitation.invitedBy}</TableCell>
													<TableCell>
														{new Date(
															invitation.invitedAt,
														).toLocaleDateString()}
													</TableCell>
													<TableCell>
														{new Date(
															invitation.expiresAt,
														).toLocaleDateString()}
													</TableCell>
													<TableCell className="text-right">
														<div className="flex justify-end gap-2">
															<Button
																variant="ghost"
																size="sm"
																onClick={() =>
																	handleResendInvite(invitation.email)
																}
															>
																Resend
															</Button>
															<Button
																variant="ghost"
																size="sm"
																className="text-destructive hover:text-destructive"
																disabled={revokeInvitationMutation.isPending && revokeInvitationMutation.variables?.invitationId === invitation.id}
																onClick={() =>
																	handleRevokeInvite(
																		invitation.id,
																		invitation.email,
																	)
																}
															>
																{revokeInvitationMutation.isPending && revokeInvitationMutation.variables?.invitationId === invitation.id ? (
																	<Loader2 className="h-4 w-4 animate-spin" />
																) : (
																	<X className="h-4 w-4" />
																)}
															</Button>
														</div>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						)}

						{hasPermission("admin") && pendingInvitations.length === 0 && (
							<Card>
								<CardContent className="py-8 text-center text-muted-foreground">
									<Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
									<p>No pending invitations</p>
								</CardContent>
							</Card>
						)}
					</TabsContent>

					<TabsContent value="notifications" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Email Notifications</CardTitle>
								<CardDescription>
									Choose which emails you want to receive
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center justify-between">
									<div>
										<p className="font-medium">New Bid Submissions</p>
										<p className="text-sm text-muted-foreground">
											When a vendor submits a bid
										</p>
									</div>
									<Switch
										checked={notificationPrefs.newBidSubmissions}
										onCheckedChange={(checked) =>
											setNotificationPrefs((prev) => ({ ...prev, newBidSubmissions: checked }))
										}
									/>
								</div>
								<Separator />
								<div className="flex items-center justify-between">
									<div>
										<p className="font-medium">Vendor Questions</p>
										<p className="text-sm text-muted-foreground">
											When vendors ask questions
										</p>
									</div>
									<Switch
										checked={notificationPrefs.vendorQuestions}
										onCheckedChange={(checked) =>
											setNotificationPrefs((prev) => ({ ...prev, vendorQuestions: checked }))
										}
									/>
								</div>
								<Separator />
								<Button
									onClick={handleSaveNotifications}
									disabled={updateNotificationsMutation.isPending}
								>
									{updateNotificationsMutation.isPending ? "Saving..." : "Save Preferences"}
								</Button>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="security" className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle>Change Password</CardTitle>
								<CardDescription>Update your account password</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="newPassword">New Password</Label>
									<Input
										id="newPassword"
										type="password"
										value={passwordForm.newPassword}
										onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="confirmPassword">Confirm New Password</Label>
									<Input
										id="confirmPassword"
										type="password"
										value={passwordForm.confirmPassword}
										onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
									/>
								</div>
								{passwordError && (
									<p className="text-sm text-red-500">{passwordError}</p>
								)}
								<Button
									onClick={handleChangePassword}
									disabled={changePasswordMutation.isPending}
								>
									{changePasswordMutation.isPending ? "Updating..." : "Update Password"}
								</Button>
							</CardContent>
						</Card>
					</TabsContent>

					{/* Embed Tab */}
					<TabsContent value="embed" className="space-y-6">
						{/* Embed Code Generator */}
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Code className="h-5 w-5" />
									Embed Code Generator
								</CardTitle>
								<CardDescription>
									Generate embed code to display your solicitations on external
									websites.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-6">
								{!orgSlug ? (
									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
										<Code className="h-10 w-10 mx-auto mb-3 text-yellow-600 opacity-75" />
										<h4 className="font-medium text-yellow-900 mb-2">
											Portal URL Not Configured
										</h4>
										<p className="text-sm text-yellow-800 mb-4">
											Your organization does not have a portal URL slug configured.
											This is required for embedding. Please contact support to have a
											slug assigned to your organization.
										</p>
									</div>
								) : (
									<>
										{/* Size Configuration */}
										<div className="grid gap-4 md:grid-cols-2">
											<div className="space-y-2">
												<Label htmlFor="embedWidth">Width</Label>
												<Input
													id="embedWidth"
													value={embedWidth}
													onChange={(e) => setEmbedWidth(e.target.value)}
													placeholder="100% or 800px"
												/>
											</div>
											<div className="space-y-2">
												<Label htmlFor="embedHeight">Height</Label>
												<Input
													id="embedHeight"
													value={embedHeight}
													onChange={(e) => setEmbedHeight(e.target.value)}
													placeholder="600px"
												/>
											</div>
										</div>

										{/* Preview */}
										<div className="space-y-2">
											<Label>Preview</Label>
											<div className="border rounded-lg p-4 bg-muted/30">
												<div
													className="border rounded-lg bg-white overflow-hidden"
													style={{
														width: embedWidth.includes("%") ? "100%" : embedWidth,
														height: "300px",
													}}
												>
													<div className="p-4 border-b bg-gray-50">
														<h3 className="font-semibold">
															{buyerOrgData?.name || "Your Organization"} - Open Solicitations
														</h3>
													</div>
													<div className="p-4 space-y-3">
														<div className="flex items-center justify-between p-3 border rounded hover:bg-muted/50">
															<div>
																<p className="font-medium">
																	IT Equipment Procurement FY2026
																</p>
																<p className="text-sm text-muted-foreground">
																	Due: Feb 15, 2026
																</p>
															</div>
															<Badge className="bg-green-100 text-green-700">
																Open
															</Badge>
														</div>
														<div className="flex items-center justify-between p-3 border rounded hover:bg-muted/50">
															<div>
																<p className="font-medium">
																	Fleet Vehicle Maintenance Services
																</p>
																<p className="text-sm text-muted-foreground">
																	Due: Jan 30, 2026
																</p>
															</div>
															<Badge className="bg-green-100 text-green-700">
																Open
															</Badge>
														</div>
														<div className="flex items-center justify-between p-3 border rounded hover:bg-muted/50">
															<div>
																<p className="font-medium">
																	HVAC System Replacement
																</p>
																<p className="text-sm text-muted-foreground">
																	Due: Feb 20, 2026
																</p>
															</div>
															<Badge className="bg-green-100 text-green-700">
																Open
															</Badge>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Generated Code */}
										<div className="space-y-2">
											<div className="flex items-center justify-between">
												<Label>Embed Code</Label>
												<Button
													variant="outline"
													size="sm"
													onClick={handleCopyEmbedCode}
												>
													<Copy className="mr-2 h-4 w-4" />
													Copy Code
												</Button>
											</div>
											<pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
												{embedCode}
											</pre>
										</div>

										{/* Instructions */}
										<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
											<h4 className="font-medium text-blue-900 mb-2">
												How to embed
											</h4>
											<ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
												<li>Copy the embed code above</li>
												<li>
													Paste the code into your website's HTML where you want the
													solicitation list to appear
												</li>
												<li>Save and publish your changes</li>
											</ol>
										</div>
									</>
								)}
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>
			</div>
		</DashboardLayout>
	);
}
